import { AlertsPortal } from '@/components/ui/alert/AlertsPortal'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { defaultLpsInfos, INS_OID } from '@/constants/dmp-connect'
import { useLazyCheckInsIdentityQuery } from '@/core/services/dmp-connect-js2'

import { useGetPatientQuery, useUpdatePatientMutation } from '@/core/services/patient'
import { useRegisterIdentityVerificationKoMutation } from '@/core/services/establishment'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { replaceAlerts } from '@/redux/reducers/alerts.reducer'
import { selectDMPConnectTlsiConnector } from '@/redux/reducers/dmp-connect.reducer'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getGenderOptions, patientGenderToInsSex } from '@/utils/patient'
import { useLogInsCall } from '@/pages/patient/model/useLogInsCall'
import { InsIdentityResultCode, InsOperation } from '@/core/models/ins-audit.model'
import { toUtcMidnight } from '@/utils/dateUtils'
import { zodResolver } from '@hookform/resolvers/zod'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { LoadingButton } from '@mui/lab'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import dayjs from 'dayjs'

import { useSnackbar } from 'notistack'
import { useEffect, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { z } from 'zod'
import { hasValidInsControlKey, splitInsNir } from '@/shared/lib/ins-search'

type CheckINSAndIdentityDialogProps = {
  open: boolean
  onClose: () => void
}

const INS_CALL_BLOCKED_MESSAGE = t`The INSi call is not possible for this identity`

export function CheckInsAndIdentityDialog({ open, onClose }: CheckINSAndIdentityDialogProps) {
  const [checkInsIdentity, { isFetching }] = useLazyCheckInsIdentityQuery()
  const { patientId } = useParams<{ patientId: string }>()
  const { data: patient } = useGetPatientQuery(patientId ?? skipToken)
  const { isInsCallBlocked } = useIsINSEnabled(patient)

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>
        <Trans>Check INS And Identity</Trans>
      </DialogTitle>
      <DialogContent>
        {isInsCallBlocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {INS_CALL_BLOCKED_MESSAGE}
          </Alert>
        )}
        <CheckInsAndIdentityForm checkInsIdentity={checkInsIdentity} formId={FORM_ID} open={open} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="neutral">
          <Trans>Cancel</Trans>
        </Button>
        <LoadingButton disabled={isInsCallBlocked} form={FORM_ID} loading={isFetching} variant="contained" type="submit">
          <Trans>Submit</Trans>
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

const formSchema = z.object({
  insNIR: z
    .object({
      value: z.string().min(1).length(13),
      // An emptied text input yields '' — treat it as "no key" rather than a length error.
      key: z
        .union([z.string().length(2), z.literal('')])
        .nullable()
        .transform((key) => key || null),
    })
    .refine(({ value, key }) => key === null || hasValidInsControlKey(value + key), {
      message: t`The INS control key is invalid.`,
      path: ['key'],
    }),
  birthFirstName: z.string().min(1),
  birthSurname: z.string().min(1),
  gender: z.enum(['M', 'F', 'I']),
  birthday: z.date().max(new Date()),
  birthPlaceCode: z.string().optional(),
  insNIA: z.boolean().optional(),
})

type CheckINSAndIdentityFormData = z.infer<typeof formSchema>

const FORM_ID = 'CheckINSAndIdentityForm'

const CHECK_INS_AND_IDENTITY_FORM_ALERTS = 'CHECK_INS_AND_IDENTITY_FORM_ALERTS'

type CheckINSAndIdentityFormProps = {
  formId: string
  open: boolean
  checkInsIdentity: ReturnType<typeof useLazyCheckInsIdentityQuery>[0]
}

function CheckInsAndIdentityForm({ checkInsIdentity, formId, open }: CheckINSAndIdentityFormProps) {
  const dispatch = useAppDispatch()
  const { patientId } = useParams<{ patientId: string }>()
  const { enqueueSnackbar } = useSnackbar()

  const { i18n } = useLingui()
  const { data: patient } = useGetPatientQuery(patientId ?? skipToken)
  const [updatePatient] = useUpdatePatientMutation()
  const [registerIdentityVerificationKo] = useRegisterIdentityVerificationKoMutation()
  const logInsCall = useLogInsCall()

  const authUser = useAppSelector(selectAuthUser)
  const establishmentId = typeof authUser?.establishment === 'string' ? authUser.establishment : authUser?.establishment?._id

  const { billingNumber, instanceUniqueId: tlsiConnectorId } = useAppSelector(selectDMPConnectTlsiConnector) ?? {}

  const defaultValues: CheckINSAndIdentityFormData = useMemo(
    () => ({
      insNIR: patient?.insNIR ? splitInsNir(patient.insNIR) : { value: '', key: null },
      birthFirstName: patient?.userInfo.birthFirstName || '',
      birthSurname: patient?.userInfo.birthSurname || '',
      gender: patient?.userInfo.gender || 'I',
      birthday: patient?.userInfo.birthday ? new Date(patient.userInfo.birthday) : new Date(),
      birthPlaceCode: patient?.userInfo.birthPlaceCode || '',
    }),
    [patient]
  )

  const genderOptions = useMemo(() => getGenderOptions().map((option) => ({ ...option, label: i18n._(option.label) })), [i18n, i18n.locale])

  const { control, handleSubmit, setValue, reset } = useForm<CheckINSAndIdentityFormData>({ defaultValues, resolver: zodResolver(formSchema) })

  useEffect(() => {
    if (open && patient) {
      reset(defaultValues)
    }
  }, [open, patient, defaultValues, reset])

  useEffect(() => {
    return () => {
      dispatch(replaceAlerts({ area: CHECK_INS_AND_IDENTITY_FORM_ALERTS, alerts: [] }))
    }
  }, [])
  const { isInsCallBlocked } = useIsINSEnabled(patient)

  const onSubmit: SubmitHandler<CheckINSAndIdentityFormData> = async (data) => {
    if (!patient || !tlsiConnectorId || isInsCallBlocked) return
    const { value: insValue, key: insKey } = data.insNIR
    const fullInsNIR = insValue + (insKey ?? '')

    const checkInsIdentityParams = {
      LpsInfos: { ...defaultLpsInfos, s_instance: tlsiConnectorId, s_billingNumber: billingNumber },
      i_sex: patientGenderToInsSex(data.gender),
      s_birthDate: dayjs(data.birthday).format('YYYY-MM-DD'),
      s_birthName: data.birthSurname.trim().toUpperCase(),
      s_birthPlace: data.birthPlaceCode,
      s_given: data.birthFirstName.trim().toUpperCase(),
      s_ins: insValue,
      s_key: insKey ?? '',
      s_oid: data.insNIA ? INS_OID['INS-NIA'] : INS_OID['INS-NIR_prod'],
    }

    try {
      const { data: result } = await checkInsIdentity(checkInsIdentityParams)
      const isInsIdentityValid = result?.s_status === 'OK'
      const resultCode = isInsIdentityValid ? InsIdentityResultCode.UNIQUE_IDENTITY_FOUND : InsIdentityResultCode.NO_IDENTITY_FOUND

      await logInsCall({
        patient: patient._id,
        operation: InsOperation.CHECK_INS_IDENTITY,
        resultCode,
        returnStatus: isInsIdentityValid ? 'OK' : 'KO',
        returnedIdentity: {
          insNIR: fullInsNIR,
          insOID: checkInsIdentityParams.s_oid,
          birthName: checkInsIdentityParams.s_birthName,
          birthGiven: checkInsIdentityParams.s_given,
          birthDate: checkInsIdentityParams.s_birthDate,
          sex: String(checkInsIdentityParams.i_sex),
          birthPlaceCode: data.birthPlaceCode,
        },
      })

      if (isInsIdentityValid) {
        dispatch(replaceAlerts({ area: CHECK_INS_AND_IDENTITY_FORM_ALERTS, alerts: [{ message: t`INS has been validated`, type: 'success' }] }))
        await updatePatient({
          id: patient._id,
          userInfo: {
            ...patient.userInfo,
            birthFirstName: data.birthFirstName,
            birthSurname: data.birthSurname,
            gender: data.gender,
            birthday: toUtcMidnight(data.birthday)?.toISOString(),
            birthPlaceCode: data.birthPlaceCode,
          },
          insNIR: fullInsNIR,
          insOID: INS_OID['INS-NIR_prod'],
        })
        enqueueSnackbar(t`The patient information has been updated successfully.`, { variant: 'success' })
      } else {
        dispatch(replaceAlerts({ area: CHECK_INS_AND_IDENTITY_FORM_ALERTS, alerts: [{ message: t`Identity could not be validated`, type: 'error' }] }))
        await updatePatient({
          id: patient._id,
          insNIR: null,
          insOID: null,
          identityConfirmed: false,
          userInfo: { ...patient.userInfo, identityJustificationType: '' },
        })
        if (establishmentId) {
          try {
            await registerIdentityVerificationKo({
              establishmentId,
              patient: patient._id,
              patientFullName:
                `${patient.userInfo.birthSurname ?? patient.userInfo.surname ?? ''} ${patient.userInfo.birthFirstName ?? ''}`.trim() || undefined,
              insNIR: fullInsNIR,
              insOID: checkInsIdentityParams.s_oid,
            }).unwrap()
          } catch (worklistError) {
            console.error('[CheckInsAndIdentityDialog] Failed to add the identity to the INS worklist:', worklistError)
          }
        }
      }
    } catch (error) {
      await logInsCall({
        patient: patient._id,
        operation: InsOperation.CHECK_INS_IDENTITY,
      })
      enqueueSnackbar(t`Check INS identity failed due to unknown error`, { variant: 'error' })
    }
  }

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <AlertsPortal area={CHECK_INS_AND_IDENTITY_FORM_ALERTS} />
        <div>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextInputController control={control} fullWidth label={t`INS registration number`} name="insNIR.value" inputProps={{ maxLength: 13 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextInputController control={control} fullWidth label={t`Key`} name="insNIR.key" inputProps={{ maxLength: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <CheckboxInputController control={control} label={t`Temporary INS`} name="insNIA" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t`Last name at birth`} name="birthSurname" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t`First birth given name`} name="birthFirstName" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name="birthday"
                render={({ field }) => (
                  <DatePickerInput
                    fullWidth
                    label={t`Birthday`}
                    onChange={(date) => {
                      setValue('birthday', date?.toDate() ?? field.value)
                    }}
                    value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInputController control={control} fullWidth label={t`Sex`} name="gender" options={genderOptions} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t`Birth location code (INSEE code)`} name="birthPlaceCode" />
            </Grid>
          </Grid>
        </div>
      </Stack>
    </form>
  )
}
